import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Client for the Zammad-backed support tickets (Api::SupportController).
 *
 * Global Health's two "Health Tools" sidebar entries are two Zammad groups —
 * the member files into one of them and then reads, answers and closes their
 * own tickets in place. The backend enforces ownership through Zammad's
 * X-On-Behalf-Of header, so nothing here needs to filter by user.
 */

/** Zammad group names. These must match the group names in Zammad exactly. */
export const HEALTH_TOOL_GROUPS = {
  concierge: 'health_concierge',
  kristy: 'ask_dr_kristy',
  // General support (login, account, billing): the backend's DEFAULT_GROUP, which
  // every brand's Zammad offers. Reached through the support hub's "I need support".
  support: 'Support'
} as const;

export type HealthToolGroup = (typeof HEALTH_TOOL_GROUPS)[keyof typeof HEALTH_TOOL_GROUPS];

export interface SupportTicket {
  id:                        number;
  number:                    string;
  title:                     string;
  group:                     string;
  state:                     string;
  created_at:                string;
  updated_at:                string;
  article_count?:            number;
  /** Last time support wrote to the member — the basis for the "new reply" badge. */
  last_agent_contact_at?:    string | null;
  last_customer_contact_at?: string | null;
}

export interface SupportAttachment {
  id:            number;
  filename:      string;
  size?:         number | string;
  content_type?: string | null;
}

export interface SupportArticle {
  id:             number;
  ticket_id:      number;
  subject?:       string | null;
  body:           string;
  content_type?:  string | null;
  /** True when the member wrote it, false when it came from support. */
  from_customer:  boolean;
  from?:          string | null;
  created_at:     string;
  attachments:    SupportAttachment[];
}

/** Base64 payload as produced by FileReader.readAsDataURL (prefix is stripped server-side). */
export interface SupportAttachmentUpload {
  filename:     string;
  content_type: string;
  data:         string;
}

export interface TicketListResponse {
  group:   string;
  tickets: SupportTicket[];
}

export interface TicketDetailResponse {
  ticket:   SupportTicket;
  articles: SupportArticle[];
}

/** One entry per ticket, per group — everything the badge poll needs. */
export interface TicketPulse {
  id:                     number;
  state:                  string;
  last_agent_contact_at?: string | null;
  updated_at:             string;
}

export interface TicketSummaryResponse {
  groups: Record<string, TicketPulse[]>;
}

@Injectable({ providedIn: 'root' })
export class SupportTicketService {
  private http = inject(HttpClient);

  list(group: HealthToolGroup): Observable<TicketListResponse> {
    return this.http.get<TicketListResponse>('/support/tickets', { params: { group } });
  }

  get(ticketId: number): Observable<TicketDetailResponse> {
    return this.http.get<TicketDetailResponse>(`/support/tickets/${ticketId}`);
  }

  summary(): Observable<TicketSummaryResponse> {
    return this.http.get<TicketSummaryResponse>('/support/tickets/summary');
  }

  create(
    group: HealthToolGroup,
    subject: string,
    message: string,
    attachments: SupportAttachmentUpload[] = []
  ): Observable<{ success: boolean; ticket_id?: number }> {
    // Name and e-mail are omitted on purpose — the backend takes them from the
    // signed-in member, which is also who the Zammad customer is created as.
    return this.http.post<{ success: boolean; ticket_id?: number }>('/support/ticket', {
      group,
      subject,
      message,
      attachments
    });
  }

  reply(
    ticketId: number,
    body: string,
    attachments: SupportAttachmentUpload[] = []
  ): Observable<{ success: boolean; article: SupportArticle }> {
    return this.http.post<{ success: boolean; article: SupportArticle }>(
      `/support/tickets/${ticketId}/reply`,
      { body, attachments }
    );
  }

  setState(ticketId: number, state: 'open' | 'closed'): Observable<{ success: boolean; ticket: SupportTicket }> {
    return this.http.post<{ success: boolean; ticket: SupportTicket }>(
      `/support/tickets/${ticketId}/state`,
      { state }
    );
  }

  /**
   * Downloaded through HttpClient rather than linked directly: on the hybrid
   * (Ionic) build the JWT travels in an Authorization header added by the auth
   * interceptor, which a plain <a href> would not carry.
   */
  downloadAttachment(ticketId: number, articleId: number, attachmentId: number): Observable<Blob> {
    return this.http.get(
      `/support/tickets/${ticketId}/attachments/${articleId}/${attachmentId}`,
      { responseType: 'blob' }
    );
  }
}
